import { useEffect, useState } from 'react';
import { Card } from '@siliconx/shared/ui/Card';
import { PanelTitle } from '@siliconx/shared/ui/PanelTitle';
import { StatusPill } from '@siliconx/shared/ui/StatusPill';
import type { BenchRunDetail } from '@siliconx/shared';
import { AgentText } from './AgentText';
import { formatBenchWhen } from './benchTime';

/**
 * No report for this long while the run is `running` reads as "no contact". The agent reports every 60 s and the API stores at
 * most one per 50 s, so the stored time lags the agent by up to ~110 s: a smaller threshold would flap.
 */
export const NO_CONTACT_AFTER_SECONDS = 180;

export function fmtDuration(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${h}h ${String(m).padStart(2, '0')}m ${String(sec).padStart(2, '0')}s`;
  if (m > 0) return `${m}m ${String(sec).padStart(2, '0')}s`;
  return `${sec}s`;
}

/** The stage the agent says it is running and when it was last heard from. Nothing for an agent that never reports. */
export function RunProgressPanel({ run, now }: { run: BenchRunDetail; now?: number }) {
  const progress = run.status === 'running' ? run.progress : undefined;
  const [tick, setTick] = useState(() => Date.now());
  useEffect(() => {
    if (!progress || now !== undefined) return undefined;
    const t = setInterval(() => setTick(Date.now()), 1_000);
    return () => clearInterval(t);
  }, [progress, now]);
  if (!progress) return null;

  const clock = now ?? tick;
  const lastSeen = new Date(progress.lastSeenAt).getTime();
  const quietFor = Number.isNaN(lastSeen) ? null : Math.max(0, Math.floor((clock - lastSeen) / 1000));
  const silent = quietFor !== null && quietFor >= NO_CONTACT_AFTER_SECONDS;
  const stage = progress.stage;
  const startedMs = stage ? new Date(stage.startedAt).getTime() : NaN;

  return (
    <Card className="space-y-2" data-testid="run-progress">
      <PanelTitle title="In progress" />
      {stage ? (
        <p className="text-sm" data-testid="run-progress-stage">
          <span className="font-mono"><AgentText value={stage.stageName} max={128} /></span>
          <span className="text-muted-foreground"> on </span>
          <AgentText value={stage.deviceKey} max={64} />
          {!Number.isNaN(startedMs) && (
            <span className="text-muted-foreground">
              {' '}- started {formatBenchWhen(stage.startedAt)}, running {fmtDuration((clock - startedMs) / 1000)}
              {stage.timeoutSeconds > 0 ? ` (limit ${fmtDuration(stage.timeoutSeconds)})` : ''}
            </span>
          )}
        </p>
      ) : (
        <p className="text-sm text-muted-foreground" data-testid="run-progress-idle">Between stages.</p>
      )}
      <p className="flex items-center gap-2 text-sm" data-testid="run-progress-seen">
        <span className="text-muted-foreground">Last heard from the bench: {formatBenchWhen(progress.lastSeenAt)}</span>
        {quietFor !== null && <span className="text-muted-foreground">({fmtDuration(quietFor)} ago)</span>}
        {silent && <StatusPill tone="amber">no contact</StatusPill>}
      </p>
    </Card>
  );
}
